import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ORDER_ERROR_CURRENCY, SIMULATED_LATENCY_MS } from '../internal/cards.constants';
import type {
  AttentionItem,
  Card,
  CardActivityEvent,
  CardActivityKind,
  CardCurrency,
  CardLimits,
  CardStatus,
  CardSubscription,
  CardTransaction,
  LimitPeriod,
  LinkedAccount,
  OrderDraft,
  PeriodLimits,
  SpendingBucket,
  TopMerchant,
} from '../internal/cards.types';
import { activityFixture } from '../internal/fixtures/activity.fixture';
import { linkedAccountsFixture } from '../internal/fixtures/accounts.fixture';
import { cardsFixture, DEMO_HOLDER_NAME } from '../internal/fixtures/cards.fixture';
import { defaultLimitsFixture } from '../internal/fixtures/limits.fixture';
import { spendingFixture, topMerchantsFixture } from '../internal/fixtures/spending.fixture';
import { subscriptionsFixture } from '../internal/fixtures/subscriptions.fixture';
import { transactionsFixture } from '../internal/fixtures/transactions.fixture';
import { delay } from '../internal/utils/delay';

export type CardPendingAction =
  'block' | 'unblock' | 'deactivate' | 'activate' | 'limits' | 'rename' | 'pin';

export type CurrencyFilter = CardCurrency | 'all';

export class CardOrderError extends Error {
  constructor() {
    super('Card order could not be processed');
    this.name = 'CardOrderError';
  }
}

const cloneLimits = (limits: CardLimits): CardLimits => ({
  daily: { ...limits.daily },
  monthly: { ...limits.monthly },
});

const sortByDateDesc = <T extends { at: string }>(items: T[]) =>
  [...items].sort((a, b) => b.at.localeCompare(a.at));

export const useCardsStore = defineStore('cards', () => {
  const cards = ref<Card[]>(cardsFixture.map((card) => ({ ...card })));
  const accounts = ref<LinkedAccount[]>(linkedAccountsFixture.map((account) => ({ ...account })));
  const transactions = ref<CardTransaction[]>(transactionsFixture);
  const activity = ref<CardActivityEvent[]>(activityFixture);
  const subscriptions = ref<CardSubscription[]>(
    subscriptionsFixture.map((subscription) => ({ ...subscription })),
  );
  const limitsByCardId = ref<Record<string, CardLimits>>({});
  const pendingActionByCardId = ref<Record<string, CardPendingAction | undefined>>({});
  const spending = ref<SpendingBucket[]>(spendingFixture);
  const topMerchants = ref<TopMerchant[]>(topMerchantsFixture);
  const welcomeCardId = ref<string | null>(null);

  const cardById = computed(() => new Map(cards.value.map((card) => [card.id, card])));
  const accountById = computed(
    () => new Map(accounts.value.map((account) => [account.id, account])),
  );
  const hasCards = computed(() => cards.value.length > 0);

  const getCard = (cardId: string) => cardById.value.get(cardId) ?? null;
  const getAccount = (accountId: string) => accountById.value.get(accountId) ?? null;

  const filteredCards = (currency: CurrencyFilter, includeDeactivated: boolean) =>
    cards.value.filter(
      (card) =>
        (currency === 'all' || card.currency === currency) &&
        (includeDeactivated || card.status !== 'deactivated'),
    );

  const accountsForCurrency = (currency: CardCurrency) =>
    accounts.value.filter((account) => account.currency === currency);

  const transactionsForCard = (cardId: string) =>
    transactions.value
      .filter((transaction) => transaction.cardId === cardId)
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  const getTransaction = (transactionId: string) =>
    transactions.value.find((transaction) => transaction.id === transactionId) ?? null;

  const activityForCard = (cardId: string) =>
    sortByDateDesc(activity.value.filter((event) => event.cardId === cardId));

  const subscriptionsForCard = (cardId: string) =>
    subscriptions.value.filter((subscription) => subscription.cardId === cardId);

  const limitsForCard = (cardId: string) =>
    limitsByCardId.value[cardId] ?? cloneLimits(defaultLimitsFixture);

  const recentActivity = computed(() => sortByDateDesc(activity.value).slice(0, 4));

  const attentionKindFor = (card: Card): AttentionItem['kind'] | null => {
    if (card.status === 'delivered') return 'activate';
    if (card.status === 'being_delivered') return 'arriving';
    if (card.status === 'active' && card.expiryYear === 2026) return 'expiring';

    return null;
  };

  const needsAttention = computed<AttentionItem[]>(() =>
    cards.value.flatMap((card) => {
      const kind = attentionKindFor(card);

      if (kind === null) return [];

      return [
        {
          id: `attention-${kind}-${card.id}`,
          cardId: card.id,
          kind,
          title: card.name,
          subtitle: `${card.name} · ${card.last4} · ${card.assignedTo}`,
          at: card.lastActivityAt ?? card.createdAt,
        },
      ];
    }),
  );

  const isPending = (cardId: string, action?: CardPendingAction) => {
    const pending = pendingActionByCardId.value[cardId];

    return action === undefined ? pending !== undefined : pending === action;
  };

  const runCardAction = async (
    cardId: string,
    action: CardPendingAction,
    mutate: (card: Card) => void,
  ) => {
    const card = getCard(cardId);

    if (!card) return;

    pendingActionByCardId.value = { ...pendingActionByCardId.value, [cardId]: action };

    try {
      await delay(SIMULATED_LATENCY_MS);
      mutate(card);
    } finally {
      pendingActionByCardId.value = { ...pendingActionByCardId.value, [cardId]: undefined };
    }
  };

  const logActivity = (cardId: string, kind: CardActivityKind, detail: string) => {
    activity.value = [
      {
        id: `act-${Date.now()}-${activity.value.length}`,
        cardId,
        at: new Date().toISOString(),
        kind,
        actor: DEMO_HOLDER_NAME,
        detail,
      },
      ...activity.value,
    ];
  };

  const setStatus = (card: Card, status: CardStatus) => {
    card.status = status;
    card.lastActivityAt = new Date().toISOString();
  };

  const blockCard = (cardId: string) =>
    runCardAction(cardId, 'block', (card) => {
      setStatus(card, 'blocked');
      logActivity(cardId, 'blocked', `${card.name} ${card.last4}`);
    });

  const unblockCard = (cardId: string) =>
    runCardAction(cardId, 'unblock', (card) => {
      setStatus(card, 'active');
      logActivity(cardId, 'unblocked', `${card.name} ${card.last4}`);
    });

  const deactivateCard = (cardId: string, transferToAccountId: string) =>
    runCardAction(cardId, 'deactivate', (card) => {
      const account = getAccount(transferToAccountId);

      if (account) account.balance += card.balanceOnCard;

      card.balanceOnCard = 0;
      setStatus(card, 'deactivated');
    });

  const activateCard = (cardId: string) =>
    runCardAction(cardId, 'activate', (card) => {
      setStatus(card, 'active');
      card.expectedDelivery = null;
      logActivity(cardId, 'activated', 'Physical card activated');
    });

  const renameCard = (cardId: string, name: string) =>
    runCardAction(cardId, 'rename', (card) => {
      card.name = name;
    });

  const resetPin = (cardId: string) =>
    runCardAction(cardId, 'pin', () => {
      logActivity(cardId, 'pin_reset', 'New PIN sent to cardholder by SMS');
    });

  const saveLimits = (cardId: string, period: LimitPeriod, limits: PeriodLimits) =>
    runCardAction(cardId, 'limits', () => {
      const current = limitsForCard(cardId);

      limitsByCardId.value = {
        ...limitsByCardId.value,
        [cardId]: { ...current, [period]: { ...limits } },
      };
      logActivity(cardId, 'limit_changed', `${period} limits updated`);
    });

  const toggleSubscriptionBlocked = (subscriptionId: string) => {
    const subscription = subscriptions.value.find(({ id }) => id === subscriptionId);

    if (subscription) subscription.blocked = !subscription.blocked;
  };

  const nextNumber = () => `4539${String(Math.floor(Math.random() * 10 ** 12)).padStart(12, '0')}`;
  const nextCvv = () => String(100 + Math.floor(Math.random() * 900));

  const buildCard = (draft: OrderDraft, type: Card['type'], status: CardStatus): Card => {
    const number = nextNumber();
    const currency = draft.currency ?? 'EUR';
    const now = new Date();
    const expectedDelivery = new Date(now);

    expectedDelivery.setDate(now.getDate() + 7);

    return {
      id: `card-${type}-${now.getTime()}`,
      name: draft.name.trim() || `${currency} ${type === 'physical' ? 'Physical' : 'Virtual'}`,
      type,
      status,
      colour: draft.colour,
      currency,
      number,
      last4: number.slice(-4),
      cvv: nextCvv(),
      expiryMonth: now.getMonth() + 1,
      expiryYear: now.getFullYear() + 3,
      holderName: DEMO_HOLDER_NAME,
      assignedTo: DEMO_HOLDER_NAME,
      linkedAccountId: draft.linkedAccountId ?? '',
      monthlyLimit: 25000,
      monthlySpent: 0,
      lastActivityAt: null,
      createdAt: now.toISOString(),
      balanceOnCard: 0,
      billingAddress: { ...draft.billing },
      expectedDelivery: type === 'physical' ? expectedDelivery.toISOString().slice(0, 10) : null,
    };
  };

  const createCard = async (draft: OrderDraft): Promise<Card> => {
    await delay(SIMULATED_LATENCY_MS);

    if (draft.currency === ORDER_ERROR_CURRENCY) throw new CardOrderError();

    const primary =
      draft.type === 'physical'
        ? buildCard(draft, 'physical', 'being_delivered')
        : buildCard(draft, 'virtual', 'active');
    const created =
      draft.type === 'physical' ? [primary, buildCard(draft, 'virtual', 'active')] : [primary];

    cards.value = [...created, ...cards.value];
    created.forEach((card) => {
      logActivity(
        card.id,
        'created',
        `${card.type === 'physical' ? 'Physical' : 'Virtual'} debit card issued`,
      );
    });
    welcomeCardId.value = primary.id;

    return primary;
  };

  const upgradeToPhysical = async (cardId: string, draft: OrderDraft): Promise<Card> => {
    const card = getCard(cardId);

    if (!card) throw new CardOrderError();

    await delay(SIMULATED_LATENCY_MS);

    const physical = buildCard(
      {
        ...draft,
        type: 'physical',
        currency: card.currency,
        linkedAccountId: card.linkedAccountId,
        name: card.name,
        colour: card.colour,
      },
      'physical',
      'being_delivered',
    );

    cards.value = [physical, ...cards.value];
    logActivity(physical.id, 'created', 'Physical debit card issued');
    welcomeCardId.value = physical.id;

    return physical;
  };

  const clearCards = () => {
    cards.value = [];
  };

  const consumeWelcome = (cardId: string) => {
    if (welcomeCardId.value !== cardId) return false;

    welcomeCardId.value = null;

    return true;
  };

  return {
    cards,
    accounts,
    spending,
    topMerchants,
    hasCards,
    recentActivity,
    needsAttention,
    getCard,
    getAccount,
    filteredCards,
    accountsForCurrency,
    transactionsForCard,
    getTransaction,
    activityForCard,
    subscriptionsForCard,
    limitsForCard,
    isPending,
    blockCard,
    unblockCard,
    deactivateCard,
    activateCard,
    renameCard,
    resetPin,
    saveLimits,
    toggleSubscriptionBlocked,
    createCard,
    upgradeToPhysical,
    clearCards,
    consumeWelcome,
  };
});
